// eslint-disable-next-line import/no-extraneous-dependencies
import { Element } from '@ckeditor/ckeditor5-engine';
// eslint-disable-next-line import/no-extraneous-dependencies
import Text from '@ckeditor/ckeditor5-engine/src/model/text';

/**
 * バリデーション結果クラス。
 */
export class ValidationResults {
  /**
   * バリデーション結果を保持するマップ。
   */
  private map: Map<Element | Text, ['individual' | 'whole', string][]>;

  /**
   * コンストラクタ。
   */
  constructor() {
    this.map = new Map<Element | Text, ['individual' | 'whole', string][]>();
  }

  /**
   * クローンを作成して返す。
   */
  clone(): ValidationResults {
    const clone = new ValidationResults();
    this.map.forEach((messages, node) => {
      clone.map.set(node, messages);
    });

    return clone;
  }

  /**
   * バリデーション結果をクリアする。
   */
  clearAll() {
    this.map.clear();
  }

  /**
   * 全体バリデーション結果をクリアする。
   */
  clearWholeValidationResult() {
    this.map.forEach((messages, node) => {
      const newMessages = messages.filter((result) => result[0] === 'individual');
      if (newMessages.length === 0) {
        this.map.delete(node);
      } else {
        this.map.set(node, newMessages);
      }
    });
  }

  /**
   * 個別バリデーション結果をクリアする。
   */
  clearIndividualValidationResult(node: Element | Text) {
    const messages = this.map.get(node);
    if (messages) {
      const newMessages = messages.filter((result) => result[0] === 'whole');
      if (newMessages.length === 0) {
        this.map.delete(node);
      } else {
        this.map.set(node, newMessages);
      }
    }
  }

  /**
   * 全体バリデーション結果を追加する。
   * @param node ノード。
   * @param message メッセージ。
   */
  pushWholeValidationResult(node: Element | Text, message: string) {
    if (this.map.has(node)) {
      const messages = this.map.get(node);
      if (messages) {
        messages.push(['whole', message]);
      }
    } else {
      this.map.set(node, [['whole', message]]);
    }
  }

  /**
   * 個別バリデーション結果を追加する。
   * @param node ノード。
   * @param message メッセージ。
   */
  pushIndividualValidationResult(node: Element | Text, message: string) {
    if (this.map.has(node)) {
      const messages = this.map.get(node);
      if (messages) {
        messages.push(['individual', message]);
      }
    } else {
      this.map.set(node, [['individual', message]]);
    }
  }

  /**
   * 対象要素にバリデーションエラーがあるかどうかを取得する。
   * @param node 対象要素。
   * @returns バリデーションエラーがある場合は true、それ以外は false。
   */
  hasMessages(node: Element | Text): boolean {
    if (!this.map.has(node)) {
      return false;
    }

    const messages = this.map.get(node);
    if (!messages) {
      return false;
    }

    return messages.length > 0;
  }

  /**
   * バリデーションエラーを持つノードのリストを取得する。
   * @returns バリデーションエラーを持つノードのリスト。
   */
  getNodes(): (Element | Text)[] {
    const nodes: (Element | Text)[] = [];
    this.map.forEach((value, key) => {
      if (value.length > 0) {
        nodes.push(key);
      }
    });

    return nodes.sort((a, b) => {
      if (a.is('model:rootElement')) {
        return -1;
      }

      if (b.is('model:rootElement')) {
        return 1;
      }

      const aPath = a.getPath();
      const bPath = b.getPath();

      for (let i = 0; i < Math.min(aPath.length, bPath.length); i += 1) {
        if (aPath[i] !== bPath[i]) {
          return aPath[i] - bPath[i];
        }
      }

      return aPath.length - bPath.length;
    });
  }

  /**
   * 対象ノードのメッセージを取得する。
   * @param node 要素。
   * @returns メッセージ。
   */
  getMessage(node: Element | Text): string {
    const results = this.map.get(node);
    if (results) {
      return results.map((result) => result[1]).join('\n');
    }
    return '';
  }
}
